import { computed, reactive, ref, watch } from 'vue'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { createToaster } from '@meforma/vue-toaster'
import {
  acceptContextUpdate,
  bookInternal,
  bookInternalPriority,
  closeBelowBase,
  closeOrder,
  createApproval,
  deleteApproval,
  exportOrders,
  fetchOrders,
  finallyCancelClosedOrder,
  joinQueue,
  leaveQueue,
  prolongWork,
  releaseBooking,
  releaseWork,
  reopenCanceledOrder,
  reopenClosedOrder,
  reopenWithContextDisagreement,
  resetManualCost,
  resetManualStatus,
  setManualCost,
  setManualStatus,
  takeWork,
} from '@/backend/distributionApi'
import { downloadBlobResponse } from '@/backend/download'
import { DAY_CITY_SORTS } from './helpers'
import {
  apiSortsToTanstack,
  buildDistributionParams,
  defaultFilters,
  loadFilters,
  loadTableState,
  normalizeFilters,
  normalizeTableState,
  saveFilters,
  saveTableState,
} from './distributionState.mjs'

const toaster = createToaster()
const DEFAULT_SORTING = apiSortsToTanstack(DAY_CITY_SORTS)
const OPERATION_MAP = {
  joinQueue,
  leaveQueue,
  takeWork,
  releaseWork,
  prolongWork,
  bookInternal,
  releaseBooking,
  bookInternalPriority,
  closeOrder,
  closeBelowBase,
  reopenClosedOrder,
  finallyCancelClosedOrder,
  reopenCanceledOrder,
  acceptContextUpdate,
  reopenWithContextDisagreement,
  setManualStatus,
  resetManualStatus,
  setManualCost,
  resetManualCost,
  createApproval,
  deleteApproval,
}

function errorMessage(error, fallback) {
  const detail = error?.response?.data?.detail
  return (typeof detail === 'string' ? detail : detail?.detail || detail?.message || error?.message) || fallback
}

export function useDistributionPage(routeTripId) {
  const queryClient = useQueryClient()
  const storedFilters = loadFilters(window.localStorage)
  const filters = reactive(storedFilters)
  const tableState = reactive(loadTableState(window.localStorage, DEFAULT_SORTING))
  const operationLoadingUid = ref(null)

  watch(filters, () => {
    try { saveFilters(window.localStorage, filters) } catch {}
  }, { deep: true })
  watch(tableState, () => {
    try { saveTableState(window.localStorage, tableState, DEFAULT_SORTING) } catch {}
  }, { deep: true })

  const effectiveFilters = computed(() => {
    if (!routeTripId.value) return normalizeFilters(filters)
    return {
      ...defaultFilters(),
      trip_id: routeTripId.value,
      loadingDateFrom: '',
      loadingDateTo: '',
      status: [],
      page: 1,
      highlightMode: filters.highlightMode,
      highlightPalette: filters.highlightPalette,
    }
  })
  const requestParams = computed(() => buildDistributionParams(effectiveFilters.value, tableState.sorting))
  const queryKey = computed(() => ['distribution-orders', requestParams.value])
  const ordersQuery = useQuery({
    queryKey,
    queryFn: ({ signal }) => fetchOrders(requestParams.value, { signal }),
    staleTime: 15000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
    placeholderData: (previousData, previousQuery) => {
      const previousParams = previousQuery?.queryKey?.[1]
      if (!previousParams) return undefined
      const previousWithoutPage = { ...previousParams, skip: 0 }
      const currentWithoutPage = { ...requestParams.value, skip: 0 }
      return JSON.stringify(previousWithoutPage) === JSON.stringify(currentWithoutPage)
        ? previousData
        : undefined
    },
  })

  const updateFilters = (patch) => {
    Object.assign(filters, patch, { page: 1 })
  }
  const setPage = (page) => { filters.page = Math.max(1, Number(page) || 1) }
  const setHighlight = (patch) => { Object.assign(filters, patch) }
  const clearFilters = () => Object.assign(filters, defaultFilters(), {
    highlightMode: filters.highlightMode,
    highlightPalette: filters.highlightPalette,
  })
  const setSorting = (sorting) => {
    tableState.sorting = sorting.length ? sorting : DEFAULT_SORTING.map((item) => ({ ...item }))
    filters.page = 1
  }
  const resetTableState = () => {
    Object.assign(tableState, normalizeTableState({}, DEFAULT_SORTING))
    filters.page = 1
  }

  const operationMutation = useMutation({
    mutationFn: async ({ uid, operation, payload }) => {
      const fn = OPERATION_MAP[operation]
      if (!uid || !fn) throw new Error('Unknown distribution operation')
      operationLoadingUid.value = uid
      return fn(uid, payload)
    },
    onSuccess: (result) => {
      if (result?.data) {
        queryClient.setQueriesData({ queryKey: ['distribution-orders'] }, (current) => {
          if (!Array.isArray(current?.items)) return current
          const uid = result.data?.order?.uid || result.data?.order?.meta?.uid
          return {
            ...current,
            items: current.items.map((item) => {
              const itemUid = item?.order?.uid || item?.order?.meta?.uid
              return (result.data._id && item._id === result.data._id) || (uid && uid === itemUid) ? result.data : item
            }),
          }
        })
      }
      queryClient.invalidateQueries({ queryKey: ['distribution-orders'] })
    },
    onSettled: () => { operationLoadingUid.value = null },
  })

  const exportMutation = useMutation({
    mutationFn: () => exportOrders(buildDistributionParams(effectiveFilters.value, tableState.sorting, { includePaging: false })),
    onSuccess: (response) => downloadBlobResponse(response, 'distribution.xlsx'),
    onError: (error) => toaster.error(errorMessage(error, 'Ошибка экспорта')),
  })

  return {
    filters,
    effectiveFilters,
    tableState,
    ordersQuery,
    operationLoadingUid,
    runOperation: operationMutation.mutateAsync,
    exportOrders: exportMutation.mutate,
    exportPending: exportMutation.isPending,
    updateFilters,
    setPage,
    setHighlight,
    clearFilters,
    setSorting,
    resetTableState,
    invalidateOrders: () => queryClient.invalidateQueries({ queryKey: ['distribution-orders'] }),
  }
}
